import { memo, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useRecoilValue } from 'recoil';

import { useClinicalNoteContext } from 'lib/context/ClinicalNoteContext/ClinicalNoteContext';

import { generateNarrative } from 'components/practice/charts/ClinicalNote/lib/advancedFormsHelpers';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Switch from 'components/shared/Switch/Switch';
import Textarea from 'components/shared/Textarea/Textarea';
import {
  clinicalNote as clinicalNoteState,
  intelligenceAiState,
  userState
} from 'components/state';

import EnhancementStatus from '../../components/EnhancementStatus';

const CustomFormNarrative = ({
  narrativeData = '',
  setNarrativeUpdated,
  syncON,
  setSyncON,
  currentForm,
  setCurrentForm,
  isMacro
}) => {
  const { setEnhanceNarrativeParams, advancedFormNarrativeRef, isNoteCanceled } =
    useClinicalNoteContext();
  const clinicalNote = useRecoilValue(clinicalNoteState);
  const [confirmationModal, setConfirmationModal] = useState({
    show: false,
    update: false,
    doNotShowAgainCheckbox: false
  });
  const user = useRecoilValue(userState);

  const intelligenceState = useRecoilValue(intelligenceAiState);
  const textAreaRef = useRef();
  const { type } = useParams();

  const formId = currentForm?.form_id || currentForm?.id;
  const formName = `custom_form_narrative:${formId}`;

  useEffect(() => {
    if (advancedFormNarrativeRef?.current) {
      advancedFormNarrativeRef.current = setCurrentForm;
    }
  }, []);

  useEffect(() => {
    if (
      intelligenceState?.path?.includes?.(formName) &&
      intelligenceState?.response !== narrativeData
    ) {
      setNarrativeUpdated(intelligenceState?.response);
      setSyncON(false);
      setCurrentForm((prev) => ({ ...prev, narrative: intelligenceState?.response }));
    }
  }, [intelligenceState]);

  const clearNarrative = () => setCurrentForm((prevState) => ({ ...prevState, narrative: null }));

  const shouldHideConfirmationModalNextTime = () => {
    const narrative_sync_checkbox = localStorage.getItem('narrative_sync_checkbox');

    const parsedNarrativeSyncCheckbox =
      typeof narrative_sync_checkbox === 'string' ? JSON.parse(narrative_sync_checkbox) : {};

    return (
      parsedNarrativeSyncCheckbox?.doNotShowConfirmModal &&
      parsedNarrativeSyncCheckbox?.userId === user?.id
    );
  };

  const switchSyncNarrative = ({ update } = {}) => {
    const hasUserCheckedDoNotShowAgain = shouldHideConfirmationModalNextTime();

    if (!syncON && !hasUserCheckedDoNotShowAgain) {
      setConfirmationModal((prevState) => ({ ...prevState, update, show: true }));
      return;
    }

    setSyncON((prev) => !prev);
  };

  const handleContinue = () => {
    setSyncON(true);

    if (confirmationModal?.doNotShowAgainCheckbox) onCheckboxChange();
    if (confirmationModal?.update) generateNarrative({ currentForm, setCurrentForm });

    setConfirmationModal((prevState) => ({
      ...prevState,
      show: false,
      update: false,
      doNotShowAgainCheckbox: false
    }));
  };

  const handleEnhanceNarrative = () => {
    setEnhanceNarrativeParams({
      kind: type,
      name: formName,
      preferredFormName: currentForm?.name || currentForm?.form?.name,
      body: narrativeData,
      customFormId: formId
    });
  };

  const options = [
    {
      icon: 'refresh-linear',
      title: syncON ? 'Unsync' : 'Sync',
      rightIcon: <Switch onChange={() => null} checked={syncON} />,
      onClick: () => switchSyncNarrative(),
      group: 'actions'
    },
    {
      icon: 'broom-mono',
      title: 'Clear',
      onClick: clearNarrative,
      group: 'actions'
    },
    {
      icon: 'update-mono',
      title: 'Update & Sync',
      group: 'actions',
      onClick: () => {
        if (!syncON) switchSyncNarrative({ update: true });
        else generateNarrative({ currentForm, setCurrentForm });
      }
    },
    {
      title: 'Enhance with MyScribe AI',
      group: 'MyScribe AI',
      icon: 'myscribeai-filled',
      onClick: () => handleEnhanceNarrative(),
      permission: {
        isAny: true,
        requiredPermissions: ['is_admin', 'myscribe.narrative_update']
      }
    }
  ];

  const onCheckboxChange = () => {
    const objectToInsert = JSON.stringify({
      doNotShowConfirmModal: event,
      userId: user?.id
    });

    localStorage.setItem('narrative_sync_checkbox', objectToInsert);
  };

  const handleQuillChange = (content, delta, source) => {
    if (typeof content === 'object' && content?.target?.value) {
      content = content?.target?.value;
    }

    if (delta?.ops?.some((op) => op.retain !== undefined) && source === 'user') {
      setSyncON(false);
    } else {
      setSyncON(true);
    }
    setNarrativeUpdated(true);

    setCurrentForm((prev) => ({ ...prev, narrative: content }));
  };

  return (
    <div className="!mt-3">
      <Textarea
        isEditor
        label={
          <EnhancementStatus type={formName}>
            <p>Narrative</p>
          </EnhancementStatus>
        }
        placeholder="Type narrative"
        transcribing
        value={narrativeData}
        forwardedRef={textAreaRef}
        id="narrative-custom-form"
        data-qa="narrative-custom-form"
        name="narrative-custom-form"
        onChange={handleQuillChange}
        disabled={!isMacro && (clinicalNote?.locked || isNoteCanceled)}
        className="min-h-[200px]"
        options={options}
        optionsGroupBy="group"
      />

      {confirmationModal?.show && (
        <Confirm
          title="Sync Narrative"
          icon="warning-2-mono"
          variant="danger"
          message={
            <div>
              <p>
                Enabling Sync Narrative will automatically generate the narrative as you interact
                with the form,{' '}
                <i className="text-danger-600">
                  causing any manually entered text in the narrative box to be lost.
                </i>
              </p>
              <div className="!mt-3">
                <Checkbox
                  id="confirm-narrative-checkbox"
                  label={<p className="text-sm">Do not show this message again</p>}
                  color="blue"
                  isChecked={confirmationModal?.doNotShowAgainCheckbox}
                  onChange={(event) =>
                    setConfirmationModal((prevState) => ({
                      ...prevState,
                      doNotShowAgainCheckbox: event.target.checked
                    }))
                  }
                />
              </div>
            </div>
          }
          handleOpen={confirmationModal?.show}
          handleClose={() => setConfirmationModal((prevState) => ({ ...prevState, show: false }))}
          footerActions={{
            primary: {
              text: 'Continue',
              onClick: handleContinue,
              color: 'danger'
            }
          }}
        />
      )}
    </div>
  );
};

export default memo(CustomFormNarrative);
